import type { IsActiveMatchOptions } from '@angular/router';

import type { NavItem } from './navigation.models';

export function pathOf(url: string): string {
  return url.split(/[?#]/)[0] ?? '';
}

/**
 * Whether `url` is `route` or lies beneath it. A `:param` segment in the route
 * (the Projects templates) matches any one non-empty segment.
 */
export function routeMatches(url: string, route: string, exact?: boolean): boolean {
  const path = pathOf(url);

  if (path === route) {
    return true;
  }

  if (route.includes('/:')) {
    return templateMatches(path, route, exact ?? false);
  }

  return !exact && route !== '/' && path.startsWith(`${route}/`);
}

function templateMatches(path: string, route: string, exact: boolean): boolean {
  const actual = path.split('/');
  const expected = route.split('/');

  if (actual.length < expected.length || (exact && actual.length !== expected.length)) {
    return false;
  }

  return expected.every((segment, index) => {
    const value = actual[index] ?? '';

    return segment.startsWith(':') ? value.length > 0 : segment === value;
  });
}

/** Declared children (after access filtering) belong in the second sidebar. */
export function hasSubmenu(item: NavItem): boolean {
  return (item.children?.length ?? 0) > 0;
}

/** A child with children of its own renders as a heading over its links. */
export function isNavGroup(item: NavItem): boolean {
  return hasSubmenu(item);
}

/** Every destination below `item`, groups flattened. */
export function navDescendants(item: NavItem): readonly NavItem[] {
  return (item.children ?? []).flatMap((child) => [child, ...navDescendants(child)]);
}

/** The links below `item`: its children, or a group's children in its place. */
export function navLeaves(item: NavItem): readonly NavItem[] {
  return (item.children ?? []).flatMap((child) => (isNavGroup(child) ? navLeaves(child) : [child]));
}

/**
 * Options for `routerLinkActive`.
 *
 * Must be a full `IsActiveMatchOptions` object. A partial shape with only
 * `{ exact, queryParams }` is mis-detected as match options (because
 * `queryParams` is set), so `exact` is ignored and `paths` stays unset —
 * which makes `/` match every URL and keeps Dashboard always active.
 *
 * `routerLinkActive` compares the link's resolved URL, so the Projects
 * pane's `:projectId` templates are filled in before they reach it.
 */
export function navLinkActiveOptions(item: NavItem): IsActiveMatchOptions {
  return {
    paths: item.exact ? 'exact' : 'subset',
    queryParams: item.queryParams === undefined ? 'ignored' : 'exact',
    fragment: 'ignored',
    matrixParams: 'ignored',
  };
}
